import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { head } from "fp-ts/es6/Array";
import { pipe } from "fp-ts/lib/function";
import { option } from "fp-ts";
import { useOnScroll, useEqualState } from "../../hooks";
import { ContentfulImage } from "../_shared-components/image-related/ContentfulImage";

import { css } from "@emotion/react";

import { CSS_MQ } from "../../styles/breakpoints";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import { FamilyCarouselPagination } from "../../layouts/product-listing-page/ProductListingPageFamiliesCarousel/FamilyCarouselPagination";
import { Link } from "@swo/shared";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { ContentfulImage2 } from "../_shared-components/image-related/ContentfulImage2";

export type VisualCarouselComponentProps = {
  elements: NonEmptyArray<{
    key: string;
    backgroundImage: {
      url: string;
      title: string | undefined;
      backgroundPositionCssString?: string;
      sys: { id: string };
    };
    headline: string;
    buttons: NonEmptyArray<Link>;
  }>;
  autoSlideMs: undefined | number;
};

export const VisualCarouselComponent: ReactTFC<VisualCarouselComponentProps> = props => {
  const ref = useRef<HTMLDivElement | null>(null);
  // Disable autoscroll for touch devices
  const [isAutoScrollOn, setAutoScroll] = useState(false);

  useEffect(() => {
    setAutoScroll(true);
  }, []);

  const [currentIndex, setCurrentIndex] = useEqualState(0);
  const [desiredIndex, setDesiredIndex] = useEqualState<null | number>(null);

  const onScroll = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (ref.current) {
        const { scrollLeft, clientWidth, scrollWidth } = ref.current;

        const imagesCenters = new Array(props.elements.length)
          .fill(undefined)
          .map((_, idx) => idx)
          .map(idx => clientWidth * idx + clientWidth / 2);

        const currentCenter = scrollLeft + clientWidth / 2;

        const nearestCenter = head(
          imagesCenters
            .map((v, idx) => ({ diff: Math.abs(v - currentCenter), idx: idx }))
            .sort((a, b) => a.diff - b.diff),
        );

        pipe(
          nearestCenter,
          option.map(center => setCurrentIndex(center.idx)),
        );
      }
    },
    [ref, props.elements.length],
  );

  useOnScroll<HTMLDivElement>(ref, onScroll);

  useEffect(() => {
    if (ref.current && desiredIndex !== null) {
      ref.current.scrollTo({ left: desiredIndex * ref.current.clientWidth, behavior: "smooth" });

      setDesiredIndex(null);
    }
  }, [desiredIndex]);

  useEffect(() => {
    const onCarouselTick = () => {
      const { current } = ref;

      if (current) {
        if (Math.abs(current.scrollWidth - (current.scrollLeft + current.clientWidth)) < 1) {
          setDesiredIndex(0);
        } else {
          setDesiredIndex(currentIndex + 1);
        }
      }
    };

    if (isAutoScrollOn && typeof props.autoSlideMs === "number" && props.autoSlideMs > 1000 /* Sanity check */) {
      const id = window.setInterval(onCarouselTick, props.autoSlideMs);

      return () => {
        window.clearInterval(id);
      };
    }
  }, [props.autoSlideMs, isAutoScrollOn, currentIndex]);

  const requestIndex = useCallback((idx: number) => {
    setDesiredIndex(idx);
  }, []);

  return (
    <React.Fragment>
      <div
        ref={ref}
        className={"u-hide-scrollbar-x"}
        css={carouselElementsStyles}
        onMouseLeave={
          props.autoSlideMs
            ? () => {
                setAutoScroll(true);
              }
            : undefined
        }
        onMouseOver={props.autoSlideMs ? () => setAutoScroll(false) : undefined}
      >
        {props.elements.map(element => {
          return (
            <div key={element.key} css={carouselElementStyles}>
              <ContentfulImage2
                assetId={element.backgroundImage.sys.id}
                css={imageStyles}
                style={
                  element.backgroundImage.backgroundPositionCssString
                    ? {
                        objectPosition: element.backgroundImage.backgroundPositionCssString,
                      }
                    : undefined
                }
                contentfulUrl={element.backgroundImage.url}
                sizes="100vw"
                alt={element.backgroundImage.title}
                withTransparency={true}
                cropping={undefined}
              />

              <div
                css={css`
                  grid-column: 2 / span 1;
                  color: ${CSS_COLORS.White};

                  ${CSS_MQ.atLeastMd} {
                    margin-top: 20px;
                  }

                  ${CSS_MQ.atMostSm} {
                    align-self: flex-end;
                    padding-bottom: 40px;
                  }
                `}
              >
                <span css={headlineStyles}>{element.headline}</span>

                <div
                  css={css`
                    ${CSS_MQ.atMostSm} {
                      display: flex;
                      flex-direction: column;
                      align-items: flex-start;
                    }

                    margin-left: -20px;
                    margin-top: -20px;

                    > * {
                      margin-top: 20px;
                      margin-left: 20px;
                    }
                  `}
                >
                  {element.buttons.map(button => {
                    return (
                      <SwarovskiButton
                        key={button.link}
                        variant={"outline-inherit"}
                        as={"a"}
                        link={button.data}
                        isInternal={button.type === "Internal Link"}
                        target={!!button.openInNewTab ? "_blank" : undefined}
                        rel={!!button.openInNewTab ? "noopener noreferrer" : undefined}
                      >
                        {button.label}
                      </SwarovskiButton>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {props.elements.length > 1 && (
        <FamilyCarouselPagination
          count={props.elements.length}
          currentIndex={currentIndex}
          requestIndex={requestIndex}
          onMouseLeave={
            props.autoSlideMs
              ? () => {
                  setAutoScroll(true);
                }
              : undefined
          }
          onMouseOver={props.autoSlideMs ? () => setAutoScroll(false) : undefined}
          visibleOnSmallViewports={true}
        />
      )}
    </React.Fragment>
  );
};

const imageStyles = css`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;

  object-fit: cover;
  object-position: center;

  z-index: -1;
`;

const headlineStyles = css`
  font-family: MinervaModern;
  text-transform: uppercase;
  display: block;

  font-size: 24px;
  letter-spacing: 0.4px;
  line-height: 26px;

  margin-bottom: 20px;

  ${CSS_MQ.atLeastMd} {
    font-size: 48px;
    letter-spacing: 2.4px;
    line-height: 48px;

    margin-bottom: 30px;

    max-width: 60%;
  }
`;

const carouselElementStyles = css`
  position: relative;

  height: 460px;
  ${CSS_MQ.atLeastMd} {
    height: 500px;
  }

  width: 100%;
  flex-shrink: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  display: grid;

  grid-template-columns: 18px 1fr 18px;
  ${CSS_MQ.atLeastMd} {
    grid-template-columns: 1fr 732px 1fr;
  }
  ${CSS_MQ.atLeastLg} {
    grid-template-columns: 1fr 980px 1fr;
  }
  ${CSS_MQ.atLeastXl} {
    grid-template-columns: 1fr 1200px 1fr;
  }

  ${CSS_MQ.atMostSm} {
    background: linear-gradient(0deg, rgba(0 0 0 / 0.5) 0%, transparent 50%, transparent 100%);
  }
  background: linear-gradient(90deg, rgba(0 0 0 / 0.5), transparent);
`;

const carouselElementsStyles = css`
  display: flex;
  overflow-x: scroll;

  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;

  > * {
    scroll-snap-align: start;
  }

  position: relative;
`;
